import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@shared/ui/dialog";
import {
  SCREEN_HEIGHTS,
  SCREEN_RATES,
  bitrateFor,
  type ScreenProfile,
} from "@entities/call";
import type { NativeSource } from "@entities/call";
import {
  listApps,
  listScreens,
  listWindows,
  type NativeScreen,
  type NativeWindow,
} from "@shared/capture";
import { Button } from "@shared/ui/button";
import {
  IconCheck,
} from "@shared/ui/icons";

/** Как часто обновлять превью. Столько же обещано в подписи над сеткой. */
const REFRESH_MS = 2000;

/**
 * Где лежит прошлый выбор экрана.
 *
 * Запоминается, потому что показывают обычно один и тот же монитор: заставлять
 * выбирать его заново каждый раз — лишний шаг на ровном месте.
 */
const CHOSEN_KEY = "airel.share.monitor";

/**
 * Брать ли звук приложения.
 *
 * По умолчанию да: показывая окно игры, обычно хотят и её звук, а не немое
 * кино. Прежнее умолчание «нет» приводило к тому, что человек включал показ и
 * только потом обнаруживал тишину.
 */
const SOUND_KEY = "airel.share.sound";

function readSound(): boolean {
  return localStorage.getItem(SOUND_KEY) !== "0";
}

function readChosen(): number | null {
  const raw = localStorage.getItem(CHOSEN_KEY);
  const value = raw === null ? Number.NaN : Number(raw);
  return Number.isInteger(value) ? value : null;
}

type Tab = "screens" | "windows" | "apps";

/**
 * Выбор того, что показывать.
 *
 * Только в нативной сборке: список экранов отдаёт система, поэтому и окно
 * своё. В вебе источник выбирает сам браузер, и это окно там не появляется.
 *
 * Плитки, а не список: экраны различают по картинке, а не по имени —
 * «Основной» и «Второй» человеку не говорят ничего, а вот что на них
 * открыто, говорит сразу.
 */
export function SharePicker({
  channel,
  server,
  profile,
  onCancel,
  onShare,
}: {
  channel: string;
  server: string | null;
  profile: ScreenProfile;
  onCancel: () => void;
  onShare: (profile: ScreenProfile, source: NativeSource, sound: boolean) => void;
}) {
  const [tab, setTab] = useState<Tab>("screens");
  const [screens, setScreens] = useState<NativeScreen[] | null>(null);
  const [windows, setWindows] = useState<NativeWindow[] | null>(null);
  const [apps, setApps] = useState<NativeWindow[] | null>(null);
  const [chosen, setChosen] = useState<NativeSource | null>(null);
  const [height, setHeight] = useState<number>(profile.height);
  /*
   * Звук берётся у процесса, которому принадлежит окно. У монитора одного
   * процесса нет, поэтому для экранов переключатель недоступен: системный
   * захват взял бы и голоса собеседников из самого разговора.
   */
  const [sound, setSound] = useState(readSound);
  const [fps, setFps] = useState<number>(profile.fps);

  /*
   * Превью живые: список перезапрашивается, пока окно открыто. Иначе на
   * плитке висел бы снимок момента, когда окно открыли, — а выбирают как
   * раз по тому, что на экране сейчас.
   */
  useEffect(() => {
    let alive = true;

    const refresh = async () => {
      // Обновляем только открытую вкладку: превью окон снимаются по одному,
      // и собирать их, пока на них не смотрят, — работа впустую.
      if (tab === "screens") {
        const next = await listScreens();
        if (!alive) return;

        setScreens(next);

        /*
         * Первый заход: берём прошлый выбор, если такой монитор ещё на
         * месте, иначе основной. «Показать» должно работать сразу, без
         * обязательного клика по плитке.
         */
        setChosen((current) => {
          if (current !== null) return current;

          const remembered = readChosen();
          const known = next.some((screen) => screen.index === remembered);

          const index =
            remembered !== null && known
              ? remembered
              : next.find((screen) => screen.primary)?.index ?? next[0]?.index;

          return index === undefined ? null : { kind: "screen", id: index };
        });

        return;
      }

      if (tab === "apps") {
        const next = await listApps();
        if (!alive) return;

        setApps(next);

        setChosen((current) =>
          current?.kind === "app" && !next.some((item) => item.hwnd === current.id)
            ? null
            : current,
        );

        return;
      }

      const next = await listWindows();
      if (!alive) return;

      setWindows(next);

      // Выбранное окно могли закрыть — тогда выбор снимается, а не остаётся
      // указывать в никуда.
      setChosen((current) =>
        current?.kind === "window" && !next.some((item) => item.hwnd === current.id)
          ? null
          : current,
      );
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), REFRESH_MS);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [tab]);

  const mbps = useMemo(() => bitrateFor(height, fps) / 1_000_000, [height, fps]);

  /*
   * Звук берётся у процесса, которому принадлежит окно, — значит у обоих
   * оконных способов и ни у одного экранного: у монитора своего процесса нет.
   *
   * Отметка гаснет вместе с недоступностью, а не остаётся стоять. Иначе
   * выходит ловушка: человек включает звук на вкладке «Окна», потом выбирает
   * экран — галочка на месте, а звука нет, и панель говорит «не берётся» при,
   * казалось бы, включённом переключателе.
   */
  const withProcess = chosen !== null && chosen.kind !== "screen";
  const soundOn = sound && withProcess;

  return (
    /*
     * Окно, а не всплывающий слой: оно перекрывает звонок целиком и требует
     * ответа. Esc, щелчок мимо и «Отмена» ведут в одну дорогу, очередь слоёв
     * держит Radix — своим слушателем на `window` это закрывалось мимо неё, то
     * есть заодно со всем, что могло стоять сверху.
     */
    <Dialog open onOpenChange={(next) => !next && onCancel()}>
      {/* Шире вопроса: под сеткой превью узкое окно смысла не имеет. */}
      <DialogContent
        className="w-[min(720px,calc(100%-48px))] max-h-[calc(100%-48px)] overflow-hidden
          surface-pop rounded-[var(--radius-panel)]"
        aria-describedby={undefined}
      >
        <div className="share-head">
          <DialogTitle className="share-title">Показать экран</DialogTitle>
          <span className="tiny muted">
            {channel}
            {server ? ` · ${server}` : ""}
          </span>
        </div>

        <div className="share-tabs">
          <button
            type="button"
            className={`share-tab ${tab === "screens" ? "on" : ""}`}
            onClick={() => setTab("screens")}
          >
            Рабочие столы
            <span className="share-count">{screens?.length ?? "—"}</span>
          </button>
          <button
            type="button"
            className={`share-tab ${tab === "windows" ? "on" : ""}`}
            onClick={() => setTab("windows")}
          >
            Окна
            <span className="share-count">{windows?.length ?? "—"}</span>
          </button>
          <button
            type="button"
            className={`share-tab ${tab === "apps" ? "on" : ""}`}
            onClick={() => setTab("apps")}
          >
            Приложения
            <span className="share-new">опыт</span>
            <span className="share-count">{apps?.length ?? "—"}</span>
          </button>
          <span className="share-refresh">обновляется каждые 2 с</span>
        </div>

        <div className="share-grid">
          {tab === "screens" ? (
            <>
              {screens === null && <div className="share-empty">Ищу экраны…</div>}

              {screens?.length === 0 && (
                <div className="share-empty">
                  Экранов не нашлось. Такое бывает, если приложение запущено без
                  прав на захват.
                </div>
              )}

              {screens?.map((screen) => (
                <Tile
                  key={screen.index}
                  thumbnail={screen.thumbnail}
                  name={screen.name}
                  meta={`${screen.width}×${screen.height}`}
                  chosen={chosen?.kind === "screen" && chosen.id === screen.index}
                  onPick={() => setChosen({ kind: "screen", id: screen.index })}
                />
              ))}

              {screens && screens.length > 0 && (
                <div className="share-warn">
                  Показ рабочего стола отдаёт всё, что на нём открыто, —
                  уведомления в том числе.
                </div>
              )}
            </>
          ) : tab === "windows" ? (
            <>
              {windows === null && <div className="share-empty">Ищу окна…</div>}

              {windows?.length === 0 && (
                <div className="share-empty">Открытых окон не нашлось.</div>
              )}

              {windows?.map((item) => (
                <Tile
                  key={item.hwnd}
                  thumbnail={item.thumbnail}
                  name={item.title}
                  meta={item.app}
                  chosen={chosen?.kind === "window" && chosen.id === item.hwnd}
                  onPick={() => setChosen({ kind: "window", id: item.hwnd })}
                />
              ))}

              {/*
                Ограничение названо прямо, и здесь же лежит причина завести
                вкладку «Приложения»: она этого недостатка лишена.
              */}
              {windows && windows.length > 0 && (
                <div className="share-warn">
                  Окно снимается как часть экрана: то, что лежит поверх него,
                  увидят и зрители, а свёрнутое окно показать нельзя. На
                  вкладке «Приложения» этого нет.
                </div>
              )}
            </>
          ) : (
            <>
              {apps === null && <div className="share-empty">Ищу приложения…</div>}

              {apps?.length === 0 && (
                <div className="share-empty">Открытых приложений не нашлось.</div>
              )}

              {apps?.map((item) => (
                <Tile
                  key={item.hwnd}
                  thumbnail={item.thumbnail}
                  name={item.title}
                  meta={item.app}
                  chosen={chosen?.kind === "app" && chosen.id === item.hwnd}
                  onPick={() => setChosen({ kind: "app", id: item.hwnd })}
                />
              ))}

              {/*
                Что здесь опытного — сказано прямым текстом, а не одной
                пометкой на вкладке. Приложение просят нарисоваться самому:
                чужие окна в кадр не попадают, но игру в эксклюзивном полном
                экране так не снять, и свёрнутое окно тоже.
              */}
              {apps && apps.length > 0 && (
                <div className="share-warn">
                  Приложение рисует себя само — чужие окна поверх зрителям не
                  видны. Способ опытный: игру в полноэкранном режиме и
                  свёрнутое окно им не снять, для них берите рабочий стол.
                </div>
              )}
            </>
          )}
        </div>

        <div className="share-foot">
          <Row
            title="Разрешение"
            options={SCREEN_HEIGHTS.map((item) => ({ id: item, label: `${item}p` }))}
            chosen={height}
            onPick={setHeight}
            note={`до ${mbps.toFixed(1)} Мбит/с`}
          />

          <Row
            title="Частота"
            options={SCREEN_RATES.map((item) => ({ id: item, label: `${item} к/с` }))}
            chosen={fps}
            onPick={setFps}
            note={fps === 60 ? "заметная нагрузка на слабых машинах" : null}
          />

          <div className="share-actions">
            <button
              type="button"
              className={`share-plan ${soundOn ? "on" : ""}`}
              role="switch"
              aria-checked={soundOn}
              disabled={!withProcess}
              title={
                withProcess
                  ? "Звук берётся у самого приложения — голоса собеседников в него не попадут"
                  : "Только для окна и приложения: у экрана нет одного приложения, чей звук взять"
              }
              onClick={() =>
                setSound((value) => {
                  localStorage.setItem(SOUND_KEY, value ? "0" : "1");
                  return !value;
                })
              }
            >
              <span className="share-switch" aria-hidden="true" />
              Звук приложения
            </button>

            {!withProcess && (
              <span className="tiny muted">только для окна и приложения</span>
            )}

            <span className="grow" />

            <Button variant="plain" size="sm" type="button" onClick={onCancel}>
              Отмена
            </Button>
            <Button size="sm"
              type="button"
              disabled={chosen === null}
              onClick={() => {
                if (chosen === null) return;

                // Запоминаем только монитор: номер окна живёт до его
                // закрытия, и в следующий раз он уже ничей.
                if (chosen.kind === "screen") {
                  localStorage.setItem(CHOSEN_KEY, String(chosen.id));
                }

                onShare(
                  { height, fps, maxBitrate: bitrateFor(height, fps) } as ScreenProfile,
                  chosen,
                  soundOn,
                );
              }}>
              Показать
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Плитка источника: превью, имя и пометка выбора. */
function Tile({
  thumbnail,
  name,
  meta,
  chosen,
  onPick,
}: {
  thumbnail: string;
  name: string;
  meta: string;
  chosen: boolean;
  onPick: () => void;
}) {
  return (
    <button type="button" className={`share-tile ${chosen ? "on" : ""}`} onClick={onPick}>
      <span className="share-shot">
        {thumbnail ? (
          <img src={thumbnail} alt="" />
        ) : (
          <span className="share-shot-none">без превью</span>
        )}
        {chosen && <span className="share-mark"><IconCheck size={13} /></span>}
      </span>
      <span className="share-name">
        <span className="grow">{name}</span>
        {meta && <span className="tiny muted">{meta}</span>}
      </span>
    </button>
  );
}

/** Ряд взаимоисключающих значений: разрешение или частота. */
function Row<T extends number>({
  title,
  options,
  chosen,
  onPick,
  note,
}: {
  title: string;
  options: { id: T; label: string }[];
  chosen: T | number;
  onPick: (value: T) => void;
  note: string | null;
}) {
  return (
    <div className="share-row">
      <span className="share-row-title">{title}</span>
      <span className="share-seg" role="group" aria-label={title}>
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`share-seg-item ${chosen === option.id ? "on" : ""}`}
            aria-pressed={chosen === option.id}
            onClick={() => onPick(option.id)}
          >
            {option.label}
          </button>
        ))}
      </span>
      {note && <span className="tiny muted">{note}</span>}
    </div>
  );
}
